import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar, CheckCircle, ClipboardList, Clock, Loader2, Plus, RefreshCw,
  Upload, XCircle, MessageSquare, Palmtree,
} from 'lucide-react';
import { leaveAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const STATUS_BADGES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
};

function StatusBadge({ status }) {
  return (
    <span
      data-testid={`leave-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGES[status] || ''}`}
    >
      {status}
    </span>
  );
}

export default function Leave() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = ['owner', 'manager', 'super_admin'].includes(user?.role);

  const [activeTab, setActiveTab] = useState('mine');
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [queue, setQueue] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(false);

  // New request modal
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    days: 1,
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Cancel + comments
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [timelineFor, setTimelineFor] = useState(null);
  const [timeline, setTimeline] = useState({ audit: [], comments: [] });
  const [newComment, setNewComment] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([leaveAPI.getLeaveTypesExtended(), leaveAPI.getLeaveRequests()]);
      setTypes((t.data.leave_types || []).filter((x) => x.active !== false));
      setRequests(r.data.requests || []);
      if (canManage) {
        const q = await leaveAPI.approvalQueue({ page: queue.page, page_size: queue.page_size });
        setQueue(q.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load leave data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.page, queue.page_size, canManage]);

  useEffect(() => { reload(); }, [reload]);

  const myRequests = useMemo(
    () => requests.filter((r) => r.employee_id === user?.id),
    [requests, user?.id],
  );

  // ── create ───────────────────────────────────────────────────────────
  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.leave_type_id || !form.start_date || !form.end_date) {
      toast.error('Type, start date and end date are required');
      return;
    }
    if (form.start_date > form.end_date) {
      toast.error('Start date must be before end date');
      return;
    }
    setSubmitting(true);
    try {
      await leaveAPI.createLeaveRequest({
        leave_type_id: form.leave_type_id,
        start_date: form.start_date,
        end_date: form.end_date,
        days: Number(form.days) || 1,
        note: form.note || null,
      });
      toast.success('Leave request submitted');
      setShowCreate(false);
      setForm({ leave_type_id: '', start_date: '', end_date: '', days: 1, note: '' });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // auto-suggest days from date range
  useEffect(() => {
    if (form.start_date && form.end_date && form.start_date <= form.end_date) {
      const s = new Date(form.start_date);
      const e = new Date(form.end_date);
      const d = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
      setForm((f) => ({ ...f, days: d }));
    }
  }, [form.start_date, form.end_date]);

  // ── approve / reject ─────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      await leaveAPI.approveLeaveRequest(id);
      toast.success('Approved');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    }
  };
  const handleReject = async (id) => {
    try {
      await leaveAPI.rejectLeaveRequest(id);
      toast.success('Rejected');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    }
  };

  // ── cancel ───────────────────────────────────────────────────────────
  const submitCancel = async () => {
    if (!cancelTarget) return;
    try {
      await leaveAPI.cancelLeaveRequest(cancelTarget.id, { reason: cancelReason || null });
      toast.success('Cancelled');
      setCancelTarget(null);
      setCancelReason('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  // ── timeline (audit + comments) ──────────────────────────────────────
  const openTimeline = async (req) => {
    setTimelineFor(req);
    try {
      const [a, c] = await Promise.all([
        canManage ? leaveAPI.audit(req.id) : Promise.resolve({ data: { items: [] } }),
        leaveAPI.listComments(req.id),
      ]);
      setTimeline({ audit: a.data.items || [], comments: c.data.items || [] });
    } catch {/* silent */}
  };

  const submitComment = async () => {
    if (!timelineFor || !newComment.trim()) return;
    try {
      await leaveAPI.addComment(timelineFor.id, newComment.trim());
      setNewComment('');
      openTimeline(timelineFor);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Comment failed');
    }
  };

  const RequestRow = ({ r, showActions }) => {
    const t = types.find((x) => x.id === r.leave_type_id);
    return (
      <tr className="border-t" data-testid={`leave-row-${r.id}`}>
        <td className="px-3 py-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t?.display_color || '#94A3B8' }} />
            <span className="text-sm">{r.leave_type_name}</span>
          </span>
        </td>
        <td className="px-3 py-2 text-sm">{r.employee_name || '—'}</td>
        <td className="px-3 py-2 font-mono text-xs">{r.start_date} → {r.end_date}</td>
        <td className="px-3 py-2 text-sm">{r.days}</td>
        <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
        <td className="px-3 py-2 text-right space-x-1">
          <Button size="sm" variant="ghost" onClick={() => openTimeline(r)} data-testid={`timeline-${r.id}`}>
            <MessageSquare className="h-3.5 w-3.5" />
          </Button>
          {showActions && r.status === 'pending' && canManage && (
            <>
              <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => handleApprove(r.id)} data-testid={`approve-${r.id}`}>
                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => handleReject(r.id)} data-testid={`reject-${r.id}`}>
                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </>
          )}
          {(r.status === 'pending' || r.status === 'approved') && (r.employee_id === user?.id || canManage) && (
            <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => setCancelTarget(r)} data-testid={`cancel-${r.id}`}>
              Cancel
            </Button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="leave-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Palmtree className="h-6 w-6 text-emerald-600" /> Leave
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Operational leave management — requests, approvals, and wall-chart visibility.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <>
                <Button size="sm" variant="outline" onClick={() => navigate('/leave/types')} data-testid="nav-leave-types">
                  Leave Types
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/leave/import')} data-testid="nav-leave-import">
                  <Upload className="h-4 w-4 mr-1" /> Import
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={() => navigate('/leave/calendar')} data-testid="nav-leave-calendar">
              <Calendar className="h-4 w-4 mr-1" /> Calendar
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="new-request-btn">
              <Plus className="h-4 w-4 mr-1" /> New Request
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="mine" data-testid="tab-mine">My Requests</TabsTrigger>
            {canManage && (
              <TabsTrigger value="queue" data-testid="tab-queue">
                <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Approval Queue
                {queue.total > 0 && <span className="ml-1.5 bg-amber-200 text-amber-800 rounded-full text-[10px] px-1.5">{queue.total}</span>}
              </TabsTrigger>
            )}
            {canManage && <TabsTrigger value="all" data-testid="tab-all">All Requests</TabsTrigger>}
          </TabsList>

          <TabsContent value="mine" className="mt-4">
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                    <th className="text-left px-3 py-2 font-medium">Dates</th>
                    <th className="text-left px-3 py-2 font-medium">Days</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-right px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" />Loading…</td></tr>
                  ) : myRequests.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400" data-testid="mine-empty">No requests yet.</td></tr>
                  ) : myRequests.map((r) => <RequestRow key={r.id} r={r} showActions={false} />)}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {canManage && (
            <TabsContent value="queue" className="mt-4">
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Type</th>
                      <th className="text-left px-3 py-2 font-medium">Employee</th>
                      <th className="text-left px-3 py-2 font-medium">Dates</th>
                      <th className="text-left px-3 py-2 font-medium">Days</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-right px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queue.items.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400" data-testid="queue-empty">Nothing pending. </td></tr>
                    ) : queue.items.map((r) => <RequestRow key={r.id} r={r} showActions />)}
                  </tbody>
                </table>
              </div>
              {queue.total > queue.page_size && (
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span className="text-slate-500">
                    Page {queue.page} of {Math.max(1, Math.ceil(queue.total / queue.page_size))}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={queue.page <= 1} onClick={() => setQueue((q) => ({ ...q, page: q.page - 1 }))}>Prev</Button>
                    <Button size="sm" variant="outline" disabled={queue.page * queue.page_size >= queue.total} onClick={() => setQueue((q) => ({ ...q, page: q.page + 1 }))}>Next</Button>
                  </div>
                </div>
              )}
            </TabsContent>
          )}

          {canManage && (
            <TabsContent value="all" className="mt-4">
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Type</th>
                      <th className="text-left px-3 py-2 font-medium">Employee</th>
                      <th className="text-left px-3 py-2 font-medium">Dates</th>
                      <th className="text-left px-3 py-2 font-medium">Days</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-right px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400">No requests.</td></tr>
                    ) : requests.map((r) => <RequestRow key={r.id} r={r} showActions />)}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Create dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New leave request</DialogTitle>
              <DialogDescription>Submit a leave request for approval.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submitCreate} className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={form.leave_type_id} onValueChange={(v) => setForm((f) => ({ ...f, leave_type_id: v }))}>
                  <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.display_color || '#94A3B8' }} />
                          {t.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="leave-start" required />
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="leave-end" required />
                </div>
              </div>
              <div>
                <Label>Days</Label>
                <Input type="number" min={0.5} step={0.5} value={form.days} onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))} data-testid="leave-days" />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="leave-note" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="leave-submit">
                  {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  Submit
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cancel dialog */}
        <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel leave request</DialogTitle>
              <DialogDescription>
                {cancelTarget?.leave_type_name} · {cancelTarget?.start_date} → {cancelTarget?.end_date}
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label>Reason (optional)</Label>
              <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelTarget(null)}>Keep</Button>
              <Button variant="destructive" onClick={submitCancel} data-testid="cancel-confirm">Cancel request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Timeline dialog */}
        <Dialog open={!!timelineFor} onOpenChange={(o) => !o && setTimelineFor(null)}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Request timeline</DialogTitle>
              <DialogDescription>
                {timelineFor?.leave_type_name} · {timelineFor?.start_date} → {timelineFor?.end_date}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-96 overflow-auto">
              {timeline.audit.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-1">AUDIT</div>
                  <ol className="space-y-1.5 text-xs">
                    {timeline.audit.map((a) => (
                      <li key={a.id} className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span className="font-medium uppercase">{a.action}</span>
                        <span className="text-slate-500">by {a.actor_email || a.actor_user_id}</span>
                        <span className="text-slate-400 ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">COMMENTS</div>
                {timeline.comments.length === 0 ? (
                  <p className="text-xs text-slate-400">No comments yet.</p>
                ) : (
                  <ol className="space-y-2 text-sm">
                    {timeline.comments.map((c) => (
                      <li key={c.id} className="rounded border p-2 bg-slate-50">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium">{c.actor_name || c.actor_user_id}</span>
                          <span className="text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <div className="mt-1">{c.text}</div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Input
                  placeholder="Add a comment…"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  data-testid="new-comment"
                />
                <Button onClick={submitComment} disabled={!newComment.trim()} data-testid="add-comment-btn">Send</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
